import React from 'react';
import { ChevronRight, Lock } from 'lucide-react';
import { Card, LockedNotice, Pill, SectionHeader } from './ui';
import { PROGRAM_NAMES, STATE_META } from '../../pages/journey/journeyApi';

const Item = ({ i, openMission }) => {
  const m = i.mission;
  const meta = STATE_META[i.state];
  const last = i.attempts[i.attempts.length - 1];
  const clickable = i.state !== 'locked';
  return (
    <button disabled={!clickable} onClick={() => openMission(m.id)} data-testid={`pathway-item-${m.id}`}
      className="w-full flex items-center gap-4 text-left py-4 border-t border-slate-100 first:border-0 group disabled:cursor-not-allowed">
      <span className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${i.state === 'locked' ? 'bg-slate-100 text-slate-400' : 'bg-[#0b1f4d] text-white'}`}>
        {i.state === 'locked' ? <Lock className="w-3.5 h-3.5" /> : m.order + 1}
      </span>
      <span className="flex-1 min-w-0">
        <span className="block font-medium text-slate-800 group-hover:text-teal-700 transition-colors">{m.title}</span>
        <span className="text-xs text-slate-500 capitalize">{m.kind === 'milestone' ? 'Milestone · human-reviewed · ' + m.level : m.kind === m.level ? m.kind : `${m.kind} · ${m.level}`}{last && last.score != null ? ` · last score ${last.score}%` : ''}</span>
      </span>
      <Pill className={meta.cls} data-testid={`pathway-state-${m.id}`}>{meta.label}</Pill>
      {clickable && <ChevronRight className="w-4 h-4 text-slate-400" />}
    </button>
  );
};

const Preview = ({ d }) => (
  <Card data-testid="learning-preview">
    <p className="font-outfit font-semibold text-[#0b1f4d] mb-1">Preview of your practical learning journey</p>
    <p className="text-sm text-slate-500 mb-4">Detailed modules unlock after enrollment.</p>
    {d.preview_journey.map((p) => (
      <div key={p.program_id} className="mb-4"><p className="text-xs uppercase tracking-widest text-teal-700 mb-2">{PROGRAM_NAMES[p.program_id]}</p>
        <ol className="space-y-1 text-sm text-slate-600">{p.titles.map((t) => <li key={t} className="flex items-center gap-2"><Lock className="w-3 h-3" /> {t}</li>)}</ol></div>))}
  </Card>
);

export const LearningJourney = ({ data: d, openMission, go }) => {
  if (!d.entitlements.length) return (<><SectionHeader title="My Learning Journey" />
    {d.summary ? <Preview d={d} /> : <LockedNotice testid="learning-locked" title="Your pathway appears after your consultation" text="We build your learning path from your consultation findings and diagnostic results." />}
    {d.summary && <button onClick={() => go('programs')} className="mt-4 text-sm font-semibold text-teal-700" data-testid="learning-see-programs">See enrollment options →</button>}</>);
  return (
    <>
      <SectionHeader eyebrow="Adaptive pathway" title="My Learning Journey">Your path adapts to the work you submit, not to videos watched or self-rated confidence.</SectionHeader>
      <div className="space-y-5">
        {d.pathways.map((pw) => (
          <Card key={pw.program_id} data-testid={`pathway-${pw.program_id}`}>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
              <p className="font-outfit font-semibold text-lg text-[#0b1f4d]">{PROGRAM_NAMES[pw.program_id]}</p>
              <div className="flex items-center gap-3 text-sm">
                {pw.level && <Pill className="bg-sky-50 text-sky-700 capitalize" data-testid={`pathway-level-${pw.program_id}`}>Placed at: {pw.level}</Pill>}
                {pw.skipped > 0 && <span className="text-xs text-slate-500" data-testid={`pathway-skipped-${pw.program_id}`}>{pw.skipped} skipped</span>}
                <span className="font-semibold text-[#0b1f4d]" data-testid={`pathway-progress-${pw.program_id}`}>{pw.progress}%</span>
              </div>
            </div>
            <div className="h-1.5 rounded-full bg-slate-100 mb-3"><div className="h-full bg-teal-500 rounded-full transition-[width] duration-700" style={{ width: `${pw.progress}%` }} /></div>
            {pw.items.map((i) => <Item key={i.mission.id} i={i} openMission={openMission} />)}
            {!pw.items.length && <p className="text-sm text-slate-500 py-4" data-testid={`pathway-empty-${pw.program_id}`}>Pick your target roles (or ask your consultant to confirm one) to unlock role missions here.</p>}
          </Card>))}
      </div>
    </>
  );
};
